import { AppInstallPermission, StargateAPI } from '@atlassian/mpac-services-client';
import { MarketplaceAppPaymentModel } from '@atlassian/mpac-types/graphql-gateway-types';
import { useEffect, useState } from 'react';

import { STARGATE_BASE_PATH } from '../../../../../../../constants';

import type {
  MarketplaceProduct as MarketplaceProductType,
  SelectedSiteAndProduct,
} from '@atlassian/mpac-site-selection';

export type DisambiguationNotNeeded = {
  furtherDisambiguationNeeded: false;
  disambiguationResult: {
    site: SelectedSiteAndProduct['site'];
    product: SelectedSiteAndProduct['product'];
  };
  availableCloudSitesCount?: number;
  availableSiteCloudIds?: string[];
};

export type DisambiguationNeeded = {
  furtherDisambiguationNeeded: true;
  disambiguationResult: null;
  availableCloudSitesCount?: number;
  availableSiteCloudIds?: string[];
};

export type DisambiguateSiteAndProduct = (
  arg1: MarketplaceProductType[],
  arg2: MarketplaceAppPaymentModel
) => Promise<DisambiguationNeeded | DisambiguationNotNeeded>;

export const disambiguateSiteAndProduct: DisambiguateSiteAndProduct = async (
  compatibleProducts,
  paymentModel
) => {
  const stargate = new StargateAPI(STARGATE_BASE_PATH);

  // 1. Needed if an app is compatible with multiple products
  if (compatibleProducts.length > 1) {
    return { furtherDisambiguationNeeded: true, disambiguationResult: null };
  }

  const availableSites = await stargate.marketplace.availableSites(compatibleProducts);
  const availableCloudSitesCount = availableSites.sites.length;

  const availableSiteCloudIds = availableSites.sites.map((site) => site.cloudId);

  // 2. Needed if a user has multiple cloud sites
  if (availableSites.sites.length !== 1) {
    return {
      furtherDisambiguationNeeded: true,
      disambiguationResult: null,
      availableCloudSitesCount,
      availableSiteCloudIds,
    };
  }

  const [onlyAvailableSite] = availableSites.sites;
  const [onlyProduct] = compatibleProducts;

  // 3. Site should have a compatible product
  const compatibleProductsIntersection = compatibleProducts.filter((product) =>
    onlyAvailableSite.products.includes(product)
  );

  if (compatibleProductsIntersection.length !== 1) {
    return {
      furtherDisambiguationNeeded: true,
      disambiguationResult: null,
      availableCloudSitesCount,
      availableSiteCloudIds,
    };
  }

  // 4. User should have appropriate permission on this site
  const permission = await stargate.marketplace.appInstallPermission(
    onlyProduct,
    onlyAvailableSite.cloudId
  );

  const canInstallApp =
    paymentModel === MarketplaceAppPaymentModel.Free
      ? permission !== AppInstallPermission.CANNOT_INSTALL
      : permission === AppInstallPermission.CAN_INSTALL_FREE_AND_PAID;

  if (canInstallApp) {
    return {
      furtherDisambiguationNeeded: false,
      disambiguationResult: {
        site: onlyAvailableSite,
        product: onlyProduct,
      },
      availableCloudSitesCount,
      availableSiteCloudIds,
    };
  } else {
    return {
      furtherDisambiguationNeeded: true,
      disambiguationResult: null,
      availableCloudSitesCount,
      availableSiteCloudIds,
    };
  }
};

export type DisambiguationResult = DisambiguationNotNeeded | DisambiguationNeeded;
type UseDisambiguatedSiteAndProduct = (
  arg1: boolean,
  arg2: MarketplaceProductType[],
  arg3: MarketplaceAppPaymentModel
) => DisambiguationResult;

export const useDisambiguatedSiteAndProduct: UseDisambiguatedSiteAndProduct = (
  userLoggedIn,
  compatibleProducts,
  paymentModel
) => {
  const [siteProductDisambiguation, setSiteProductDisambiguation] = useState<DisambiguationResult>({
    furtherDisambiguationNeeded: true,
    disambiguationResult: null,
  });

  useEffect(() => {
    if (userLoggedIn) {
      disambiguateSiteAndProduct(compatibleProducts, paymentModel)
        .then((result) => {
          setSiteProductDisambiguation(result);
        })
        .catch(console.error.bind(console));
    }
  }, [userLoggedIn]);

  return siteProductDisambiguation;
};
